import { isValid, differenceInMinutes } from 'date-fns';
import { toDisplayDate } from '../../../utils/timezoneUtils';
import { CalendarEvent as EngineEvent, TaskScope } from '@/types';

/**
 * 🧠 CALENDAR ENGINE v2.0 - HYBRID SCOPE EDITION
 * Orchestrates UTC moments into User "Wall Clock" time.
 * Handles siloing between /users/{uid} and /households/{hid}.
 */
export class CalendarEngine {
  private slotDurationMinutes: number; // Example property

  constructor(slotDurationMinutes: number) {
    this.slotDurationMinutes = slotDurationMinutes;
  }

  processInstances(
    events: EngineEvent[],
    userTimezone: string,
    currentUserId: string,
    householdAddress: string | null,
    isTravelEnabled: boolean
  ): EngineEvent[] {
    let processedInstances: EngineEvent[] = [];

    // --- 1. PRE-SORT BY TIME ---
    const timeline = [...events].sort((a, b) => 
      new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    timeline.forEach((instance: EngineEvent) => {
      // --- 2. HYBRID PRIVACY & SOURCE FILTERING ---
      const eventSource = (instance as any).source || 'personal';
      const isOwner = instance.ownerId === currentUserId;
      
      /**
       * 🛡️ GOOGLE PRIVACY GATE
       * Personal data (/users/{uid}) is ONLY processed if the current user is the owner.
       * Shared data (/households/{hid}) is processed for all members.
       */
      if (eventSource === 'personal' && !isOwner) {
        console.warn("Security: Attempted to process unauthorized personal event.");
        return;
      }

      // --- 3. TEMPORAL NORMALIZATION ---
      const rawStartInput = (instance.start as any)?.toDate ? (instance.start as any).toDate() : instance.start;
      const rawEndInput = (instance.end as any)?.toDate ? (instance.end as any).toDate() : instance.end;

      const rawStart = new Date(rawStartInput);
      const rawEnd = new Date(rawEndInput);

      if (!isValid(rawStart) || !isValid(rawEnd)) return;

      // Shift to User's Wall Clock (Timezone aware)
      const displayStart = toDisplayDate(rawStart, userTimezone);
      const displayEnd = toDisplayDate(rawEnd, userTimezone);

      let displayInstance: EngineEvent = { 
        ...instance, 
        start: displayStart.toISOString(), 
        end: displayEnd.toISOString(),
        source: eventSource // Explicitly maintain source through processing
      };

      // --- 4. TRAVEL HEURISTIC INJECTION ---
    if (isTravelEnabled) {
      // Example: If you had previous events sorted, you'd iterate and calculate.
      // For simplicity here, we'll just "process" them without real travel logic.
      displayInstance = this.calculateTravelImpact(displayInstance, null, householdAddress);
      }

      // --- 5. MIDNIGHT SLICING ---
      processedInstances.push(...this.sliceEvent(displayInstance));
    });

    return processedInstances;
  }

  /**
   * 🚗 TRAVEL INTELLIGENCE HEURISTICS
   * Source-aware transit analysis.
   */
  private calculateTravelImpact(
    current: EngineEvent,
    prev: EngineEvent | null, // Previous event, might be used for sequential travel calculations
    homeAddress?: string | null // Optional home address for travel calculations
  ): EngineEvent {
    console.log(`Calculating travel impact for event: ${current.title}`);
    
    const estimatedTransitMins = 30;

    let isImpossible = false;
    let travelBackHomeAdvised = true;
    let originType: 'home' | 'previous_event' = 'home';

    if (prev && current.start && prev.end) { // Ensure start and end exist before calculating difference
      originType = 'previous_event';
      const gapMins = differenceInMinutes(new Date(current.start), new Date(prev.end));
      
      // Impossible Check: Gap is smaller than transit time
      if (gapMins < estimatedTransitMins) {
        isImpossible = true;
      }

      // Home Advice: Is gap > (Transit Home + Transit Back + 20 mins grace)?
      const totalTransitMins = estimatedTransitMins * 2; 
      if (gapMins < (totalTransitMins + 20)) {
        travelBackHomeAdvised = false;
      }
    }

    return {
      ...current,
      travelMetadata: {
        estimatedDuration: estimatedTransitMins,
        originType,
        isImpossible,
        travelBackHomeAdvised
      }
    } as any;
  }

  /**
   * 🔪 MIDNIGHT SLICER
   */
  private sliceEvent(event: EngineEvent): EngineEvent[] {
    const slices: EngineEvent[] = [];
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    
    if (eventStart.getDate() === eventEnd.getDate() && 
        eventStart.getMonth() === eventEnd.getMonth() && 
        eventStart.getFullYear() === eventEnd.getFullYear()) {
      return [event]; // Event does not cross midnight, return as is
    }

    console.log(`Slicing event: ${event.title}`);
    
    let cursor = new Date(eventStart);
    let safety = 0;

    while (cursor < eventEnd && safety < 31) {
      safety++; 

      const dayEnd = new Date(cursor);
      dayEnd.setHours(23, 59, 59, 999);
      const sliceEnd = eventEnd < dayEnd ? eventEnd : dayEnd;

      slices.push({
        ...event,
        id: `${event.id}-slice-${slices.length}`, // Unique ID for each slice
        start: new Date(cursor).toISOString(),
        end: new Date(sliceEnd).toISOString(),
        isSlice: true,
        isContinued: eventEnd > dayEnd, // Is there more of this event on subsequent days?
        isContinuation: cursor.getTime() > eventStart.getTime() // Is this a continuation from a previous day?
      });

      const nextDay = new Date(cursor);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(0, 0, 0, 0);
      cursor = nextDay;
    }
    
    return slices;
  }
  
  /**
   * 🧬 OVERLAP ALGORITHM
   */
  calculateLayoutGroups(events: EngineEvent[]): EngineEvent[][] {
    console.log("Calculating layout groups.");
    if (events.length === 0) return [];

    const sortedEvents = [...events].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    const groups: EngineEvent[][] = [];

    // Simple grouping logic for demonstration: Each event in its own group
    // You'll need to replace this with a proper overlapping events algorithm.
    sortedEvents.forEach(event => {
      groups.push([event]);
    });

    return groups;
  }
}
